import type { SamplerRuntimeSample } from "../types";

/** Fixed ids for built-in one-cycle factory waveforms. */
export const FACTORY_CYCLE_DEFS = [
  { id: "factory/sine", name: "Sine", wave: "sine" as const },
  { id: "factory/tri", name: "Triangle", wave: "tri" as const },
  { id: "factory/saw", name: "Saw", wave: "saw" as const },
  { id: "factory/square", name: "Square", wave: "square" as const },
  { id: "factory/pulse25", name: "Pulse 25%", wave: "pulse25" as const },
  { id: "factory/noise", name: "Noise Cycle", wave: "noise" as const },
  { id: "factory/digital1", name: "Digital 1", wave: "digital1" as const },
  { id: "factory/digital2", name: "Digital 2", wave: "digital2" as const },
] as const;

export type FactoryCycleId = (typeof FACTORY_CYCLE_DEFS)[number]["id"];

export const FACTORY_CYCLE_IDS = new Set<string>(FACTORY_CYCLE_DEFS.map(d => d.id));

/** Default factory sample for new sampler instruments. */
export function defaultFactorySampleId(): FactoryCycleId {
  return "factory/sine";
}

export const FACTORY_CYCLE_LENGTH = 2048;
export const FACTORY_CYCLE_SAMPLE_RATE = 48_000;

type WaveKind = (typeof FACTORY_CYCLE_DEFS)[number]["wave"];

function fillWave(pcm: Float32Array, wave: WaveKind): void {
  const n = pcm.length;
  for (let i = 0; i < n; i++) {
    const phase = i / n;
    switch (wave) {
      case "sine":
        pcm[i] = Math.sin(2 * Math.PI * phase);
        break;
      case "tri":
        pcm[i] = 1 - 4 * Math.abs(phase - 0.5);
        break;
      case "saw":
        pcm[i] = 2 * phase - 1;
        break;
      case "square":
        pcm[i] = phase < 0.5 ? 1 : -1;
        break;
      case "pulse25":
        pcm[i] = phase < 0.25 ? 1 : -1;
        break;
      case "noise": {
        // Deterministic "noise cycle" so every load is identical.
        const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
        pcm[i] = (x - Math.floor(x)) * 2 - 1;
        break;
      }
      case "digital1": {
        const s = Math.sin(2 * Math.PI * phase) + 0.35 * Math.sin(2 * Math.PI * phase * 3);
        pcm[i] = Math.tanh(s * 1.4) * 0.9;
        break;
      }
      case "digital2": {
        const bit = Math.floor(phase * 16) / 16;
        pcm[i] = Math.sin(2 * Math.PI * bit) * 0.85;
        break;
      }
      default:
        pcm[i] = 0;
    }
  }
}

const factoryCache = new Map<string, SamplerRuntimeSample>();

/** Build (or return cached) factory one-cycle PCM for a known id. */
export function getFactoryCycleSample(id: string): SamplerRuntimeSample | undefined {
  if (!FACTORY_CYCLE_IDS.has(id)) {
    return undefined;
  }
  const cached = factoryCache.get(id);
  if (cached) {
    return cached;
  }
  const def = FACTORY_CYCLE_DEFS.find(d => d.id === id);
  if (!def) {
    return undefined;
  }
  const pcm = new Float32Array(FACTORY_CYCLE_LENGTH);
  fillWave(pcm, def.wave);
  const sample: SamplerRuntimeSample = {
    id: def.id,
    name: def.name,
    sampleRate: FACTORY_CYCLE_SAMPLE_RATE,
    pcm,
    oneCycle: true,
    rootKey: 60,
  };
  factoryCache.set(id, sample);
  return sample;
}

export function listFactoryCycleMetas(): Array<{
  id: string;
  name: string;
  sampleRate: number;
  lengthFrames: number;
  oneCycle: true;
  source: "factory";
  rootKey: number;
}> {
  return FACTORY_CYCLE_DEFS.map(def => ({
    id: def.id,
    name: def.name,
    sampleRate: FACTORY_CYCLE_SAMPLE_RATE,
    lengthFrames: FACTORY_CYCLE_LENGTH,
    oneCycle: true as const,
    source: "factory" as const,
    rootKey: 60,
  }));
}

export function isFactorySampleId(id: string): boolean {
  return FACTORY_CYCLE_IDS.has(id);
}
